import { Component, OnInit, ViewChild, TemplateRef, ViewContainerRef, AfterViewInit, Inject, ElementRef, ContentChildren, QueryList, EventEmitter, Output, HostListener, Input, AfterContentInit, forwardRef, NgZone, OnDestroy, HostBinding } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, FlexibleConnectedPositionStrategy, ViewportRuler, OverlayContainer, GlobalPositionStrategy, BlockScrollStrategy, ScrollDispatcher, CloseScrollStrategy } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { BreakpointObserver } from '@angular/cdk/layout';
import { merge, of, Subject } from 'rxjs';
import { DropdownItemComponent, ItemSelectData } from '../../dropdown/dropdown-item/dropdown-item.component';
import { ENTER, ESCAPE, SPACE } from '@angular/cdk/keycodes';
import { dropdownModalBreakpoint } from '@app/core/constants/style-constants';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * (Mostly) unstyled dropdown to create responsive dropdown menus.
 *
 * The dropdown options are displayed in a modal in the center of the screen if the device is small
 * (otherwise it behaves like a normal dropdown). The dropdown may contain any content.
 *
 * Usage:
 * ```
 * <app-responsive-dropdown-scaffold [(ngModel)]="selectedItem" [title]="'Optional title for mobile view'"]>
 *   <div dropdown></div>
 *   <div dropdown-content>
 *     <app-dropdown-item [value]="value"></app-dropdown-item>
 *     <app-dropdown-divider><app-dropdown-divider>
 *     <app-dropdown-item [value]="value2"></app-dropdown-item>
 *   </div>
 * </app-responsive-dropdown-scaffold>
 * ```
 */
@Component({
  selector: 'app-responsive-dropdown-scaffold',
  templateUrl: './responsive-dropdown-scaffold.component.html',
  styleUrls: ['./responsive-dropdown-scaffold.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResponsiveDropdownScaffoldComponent),
      multi: true
    }
  ]
})
export class ResponsiveDropdownScaffoldComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentInit, ControlValueAccessor {
  /** Emits if dropdown is opened (true)/closed (false) */
  private readonly onDropdownToggleSubject = new Subject<boolean>();

  private overlayRef: OverlayRef;
  private dropdownPortal: TemplatePortal;

  private keyManager: ActiveDescendantKeyManager<DropdownItemComponent>;

  private _panelOpen = false;
  /** Whether the dropdown is in desktop or mobile view */
  private isMobileView = false;

  // Functions needed to manage the ngModel of this component
  private onChange: (_: any) => void;
  private onTouched: any;

  /** Used to keep the dropdown at least as wide as the trigger */
  public minWidth: number|null = null;

  public itemIds = '';

  /**
   * Value of the item that is selected in the dropdown
   */
  public get selectedItem(): any {
    return this._selectedItem;
  }
  public set selectedItem(value: any) {
    if (this.items) {
      this.setSelection(value);
    }

    this._selectedItem = value;
  }
  private _selectedItem: any;

  public get panelOpen(): boolean {
    return this._panelOpen;
  }

  /** Title of the dropdown which is shown in mobile view (optional) */
  @Input()
  public title?: string;

  /**
   * HTMLElement that controls scrolling of the dropdown items
   * (if the scrolling behavior is not changed externally this property must not be set)
   */
  @Input()
  public itemContainer: ElementRef<HTMLElement>;

  @Input()
  @HostBinding('class.disabled')
  public disabled = false;

  @Output()
  public dropdownOpen = new EventEmitter<any>();

  /** Emits if selection was changed by the user */
  @Output()
  public selectionChanged = new EventEmitter<any>();

  /**
   * Contains dropdown items
   */
  @ViewChild('dropdownPortal')
  public dropdownPortalRef: TemplateRef<unknown>;
  /**
   * Dropdown trigger
   */
  @ViewChild('dropdown')
  public dropdownRef: ElementRef<HTMLElement>;

  /**
   * All select options
   */
  @ContentChildren(DropdownItemComponent, {descendants: true})
  public items: QueryList<DropdownItemComponent>;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private document: Document,
    private platform: Platform,
    private overlayContainer: OverlayContainer,
    private breakpointObserver: BreakpointObserver,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone
  ) { }

  public ngOnInit(): void {

  }

  public ngOnDestroy() {
    this.onDropdownToggleSubject.complete();
  }

  public ngAfterViewInit() {
    this.dropdownPortal = new TemplatePortal(this.dropdownPortalRef, this.viewContainerRef);

    this.keyManager = new ActiveDescendantKeyManager(this.items).withWrap();

    // Always keep the selected item visible
    this.keyManager.change.subscribe((index) => {
      if (this._panelOpen) {
        this.scrollToItem(index);
      }
    });

    // If tab was pressed, we close the menu and select the selected item
    this.keyManager.tabOut.subscribe(() => {
      // tabOut is also triggered if menu is not open. But if menu is not open, there is no activeItem
      if (this.keyManager.activeItem) {
        this.keyManager.activeItem.select();
        this.hide();
      }
    });

    // Resubscribe to dropdown item changes when dropdown items changed and on initial init
    merge(this.items.changes, of(null))
      .pipe(
        // Hier fügen wir den Typ-Cast für das Array hinzu, das wir in merge spreaden
        switchMap(() => merge<ItemSelectData[]>(...this.items.map(item => item.itemSelect)))
      )
      .subscribe((itemSelect: ItemSelectData) => { // Typ hier explizit angeben
        if (!itemSelect.external) {
          this.hide(true);
        }

        // Selected item was changed
        this.selectedItem = itemSelect.value;
        if (this.onChange) {
          this.onChange(this.selectedItem);
        }

        if (itemSelect.triggeredByUser) {
          this.selectionChanged.emit(this.selectedItem);
        }
      });

    // It may happen that the selectedItem was set before the items in the dropdown could be queried
    // So we need to select them afterwards
    this.setSelection(this.selectedItem);
  }

  public ngAfterContentInit() {
    this.items.changes.subscribe(() => {
      this.itemIds = this.items.map(item => item.id).join(' ');
    });
  }

  /** Part of ControlValueAccessor interface */
  public writeValue(value: any) {
    this.selectedItem = value;
  }

  /** Part of ControlValueAccessor interface */
  public registerOnChange(fn: (_: any) => void) {
    this.onChange = fn;
  }

  /** Part of ControlValueAccessor interface */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /** Part of ControlValueAccessor interface. */
  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  public onDropdownClick() {
    this.dropdownRef.nativeElement.focus();
    this.show();
  }

  /**
   * Scroll to the given item in the dropdown
   */
  public scrollToItem(itemIndex: number) {
    // The itemContainer must be the cointainer that controls the overflow property in CSS
    const itemContainer = this.itemContainer?.nativeElement ?? this.overlayRef.overlayElement.querySelector('.menu-items');
    const dropdownTopPadding = this.getElementTopPadding(this.overlayRef.overlayElement.querySelector('.dropdown-portal'));
    const currentScrollTop = itemContainer.scrollTop;
    const itemArr = this.items.toArray();
    const itemOffsetTop = itemArr[itemIndex].elementOffsetTop - dropdownTopPadding;
    const itemHeight = itemArr[itemIndex].elementHeight;

    if (itemOffsetTop < currentScrollTop) {
      itemContainer.scrollTop = itemOffsetTop;
    } else if (itemOffsetTop + itemHeight > currentScrollTop + itemContainer.clientHeight) {
      itemContainer.scrollTop = itemOffsetTop + itemHeight - itemContainer.clientHeight;
    }
  }

  /**
   * Closes dropdown
   * @param keepFocus If true, focus() on the dropdown element is called
   */
  public hide(keepFocus = false) {
    this.overlayRef.detach();

    this._panelOpen = false;

    if (keepFocus) {
      this.dropdownRef.nativeElement.focus();
    }

    this.onDropdownToggleSubject.next(false);
  }

  private show() {
    // Don't open the dropdown if we have no dropdown items
    if (this.items.length === 0 || this.disabled) {
      return;
    }

    this.overlayRef = this.overlay.create({
      panelClass: 'scg-overlay-pane',
      hasBackdrop: true,
      disposeOnNavigation: true
    });

    this.overlayRef.attach(this.dropdownPortal);
    this.overlayRef.backdropClick().subscribe(() => {
      this.hide();
    });

    this.overlayRef.detachments().subscribe(() => {
      // After dispose the hide method may not be called. Therefore we need to cleanup here
      if (this._panelOpen) {
        this._panelOpen = false;
        this.onDropdownToggleSubject.next(false);
      }
    });

    // Update dropdown content size if the trigger size changes eventually
    this.viewportRuler.change()
      .pipe(
        takeUntil(this.onDropdownToggleSubject.pipe(filter(open => !open))),
        filter(() => !this.isMobileView)
      )
      .subscribe(() => {
        this.minWidth = this.dropdownRef.nativeElement.offsetWidth;
        this.overlayRef.updateSize({ minWidth: this.dropdownRef.nativeElement.offsetWidth });
      });

    // Intial and dynamic update of positionStrategy and backdrop style
    this.breakpointObserver.observe([
      `(orientation: portrait) and (max-width: ${dropdownModalBreakpoint})`,
      `(orientation: landscape) and (max-height: ${dropdownModalBreakpoint})`
    ])
      .pipe(takeUntil(this.onDropdownToggleSubject.pipe(filter(open => !open))))
      .subscribe({
      next: (state) => {
        if (state.matches) {
          this.isMobileView = true;

          this.overlayRef.updatePositionStrategy(this.getGlobalPositionStrategy());
          this.minWidth = null;
          this.overlayRef.updateSize({ minWidth: null });
          this.overlayRef.updateScrollStrategy(new BlockScrollStrategy(this.viewportRuler, this.document));
          this.overlayRef.backdropElement.classList.remove('cdk-overlay-transparent-backdrop');
        } else {
          this.isMobileView = false;

          this.overlayRef.updatePositionStrategy(this.getFlexibleConnectedPositionStrategy());
          this.minWidth = this.dropdownRef.nativeElement.offsetWidth;
          this.overlayRef.updateSize({ minWidth: this.dropdownRef.nativeElement.offsetWidth });
          // this.overlayRef.updateScrollStrategy(new NoopScrollStrategy());
          this.overlayRef.updateScrollStrategy(new CloseScrollStrategy(this.scrollDispatcher, this.ngZone, this.viewportRuler));
          this.overlayRef.backdropElement.classList.add('cdk-overlay-transparent-backdrop');
        }
      }
    });

    const selectedItems = this.items.filter((item) => item.selected);
    if (selectedItems.length === 1) {
      this.scrollToItem(this.items.toArray().indexOf(selectedItems[0]));
      this.keyManager.setActiveItem(selectedItems[0]);
    }

    this._panelOpen = true;
    this.onDropdownToggleSubject.next(true);
    this.dropdownOpen.emit();
  }

  private getGlobalPositionStrategy(): GlobalPositionStrategy {
    const strategy = new GlobalPositionStrategy();
    strategy.centerHorizontally();
    strategy.centerVertically();
    return strategy;
  }

  private getFlexibleConnectedPositionStrategy(): FlexibleConnectedPositionStrategy {
    const strategy = new FlexibleConnectedPositionStrategy(this.dropdownRef,
      this.viewportRuler,
      this.document,
      this.platform,
      this.overlayContainer
    );

    // The next two lines make sure, we set a max-height and show a scrollbar
    // instead of repositioning somewhere else on screen or clipping out of viewport
    strategy.withFlexibleDimensions(true);
    strategy.withPush(false);

    strategy.withViewportMargin(10);
    strategy.withPositions([
      {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
      {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top'},
      {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'},
      {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom'}
    ]);
    return strategy;
  }

  /** Deselects all items and only selects the given item */
  private setSelection(selectedItem: any) {
    if (!this.items) {
      return;
    }

    for (const item of this.items) {
      if (selectedItem != null && item.value === selectedItem && !item.selected) {
        item.select(true, false);
      } else if (item.value !== selectedItem) {
        item.deselect();
      }
    }
  }

  /** Returns top padding of the given element in pixels */
  private getElementTopPadding(element: HTMLElement): number {
    let rawPadding = this.document.defaultView.getComputedStyle(element).getPropertyValue('padding-top');
    rawPadding = rawPadding.substr(0, rawPadding.indexOf('px'));
    const padding = Number.parseInt(rawPadding, 10);
    return padding === Number.NaN ? 0 : padding;
  }

  public onBlur() {
    if (this.onTouched) {
      this.onTouched();
    }
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (!this._panelOpen && (event.keyCode === ENTER || event.keyCode === SPACE)) {
      this.show();

      // Prevent default because otherwise pressing space would scroll the page down
      event.preventDefault();
    } else if (this._panelOpen && (event.keyCode === ENTER || event.keyCode === SPACE || event.keyCode === ESCAPE)) {
      this.keyManager.activeItem.select();
      this.hide();

      // Prevent default because otherwise pressing space would scroll the page down
      event.preventDefault();
    } else {
      this.keyManager.onKeydown(event);
    }
  }
}
